'use client'
import { clsx } from 'clsx'
import * as React from 'react'
import { useEditorWidth } from '~/hooks/use-editor-width'

export const FullBleedContainer = React.forwardRef<
  HTMLDivElement,
  {
    disabled?: boolean
    children?: React.ReactNode
    flexGrow?: boolean
    initialOverflow?: string
    maxWidth?: 1024 | 1969
    bleedContainerClassName?: string
  } & React.JSX.IntrinsicElements['div']
>(
  (
    {
      children,
      disabled,
      flexGrow,
      initialOverflow,
      maxWidth,
      bleedContainerClassName,
      ...props
    },
    ref
  ) => {
    if (disabled) {
      return (
        <DefaultContainer ref={ref} {...props}>
          {children}
        </DefaultContainer>
      )
    }

    return (
      <BleedContainer
        ref={ref}
        flexGrow={flexGrow}
        initialOverflow={initialOverflow}
        maxWidth={maxWidth}
        bleedContainerClassName={bleedContainerClassName}
        {...props}
      >
        {children}
      </BleedContainer>
    )
  }
)

const DefaultContainer = React.forwardRef<
  HTMLDivElement,
  {
    children?: React.ReactNode
    className?: string
  } & React.JSX.IntrinsicElements['div']
>(({ children, ...props }, ref) => {
  return (
    <div ref={ref} {...props}>
      {children}
    </div>
  )
})

const BleedContainer = React.forwardRef<
  HTMLDivElement,
  {
    children?: React.ReactNode
    bleedContainerClassName?: string
    flexGrow?: boolean
    initialOverflow?: string
    maxWidth?: 1024 | 1969
  } & React.JSX.IntrinsicElements['div']
>(
  (
    {
      children,
      className,
      bleedContainerClassName,
      style,
      flexGrow = true,
      initialOverflow,
      maxWidth = 1024,
      ...props
    },
    ref
  ) => {
    const editorWidth = useEditorWidth()

    const { width, paddingInline, withInitialOverflow } = React.useMemo(() => {
      if (!editorWidth) {
        return { width: '100%', paddingInline: 0, withInitialOverflow: true }
      }

      /**
       * This & the padding both are variable, we should get
       * them from the TabContentDefaultWrapper soon enough
       */
      const BLOCK_MAX_WIDTH = maxWidth
      const BLOCK_HORIZONTAL_PADDING = 96
      const BORDER_WIDTH = 1

      const paddingIfEditorWidthIsWiderThanMaxWidth =
        (editorWidth -
          BLOCK_MAX_WIDTH +
          BLOCK_HORIZONTAL_PADDING * 2 +
          BORDER_WIDTH * 2) /
        2

      const width =
        editorWidth <= BLOCK_MAX_WIDTH
          ? `calc(100% + ${BLOCK_HORIZONTAL_PADDING * 2}px)`
          : editorWidth

      const paddingInline =
        editorWidth <= BLOCK_MAX_WIDTH
          ? `${BLOCK_HORIZONTAL_PADDING}px`
          : `${paddingIfEditorWidthIsWiderThanMaxWidth}px`

      return { width, paddingInline }
    }, [editorWidth, maxWidth])

    return (
      <div
        ref={ref}
        className={clsx(
          bleedContainerClassName,
          'no-scrollbar relative left-1/2 -translate-x-1/2 flex overflow-x-auto overflow-y-hidden'
        )}
        style={{
          width,
          paddingInline,
          overflow: withInitialOverflow ? initialOverflow : undefined,
          ...(style ?? {}),
        }}
        {...props}
      >
        <div
          className={clsx(flexGrow && 'grow', 'shrink-0 relative', className)}
        >
          {children}
        </div>
      </div>
    )
  }
)
